import { useQuery, skipToken } from '@tanstack/react-query';
import { isPermissionRefused, type LockCodes } from '@citibox-panel/api-client';

export interface UseLockCodes {
  codes: LockCodes | undefined;
  isPending: boolean;
  // A read is in flight — including the re-read of an unfold that already has cached codes. The
  // block holds its skeleton on this rather than painting credentials it is revalidating.
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

// The two code tables of the «Entrega»'s cerradura, keyed by LOCK id — the devices-service id the
// detail carries (`lock.id`, CBX-10985). Unlike the box log beside it, this read has nothing to do
// with the «buzón»: a box can be re-paired, the sequences belong to the cerradura.
//
// Skipped (skipToken) until the disclosure is unfolded AND the cerradura has a devices id, so
// opening the «Entrega» detail costs no request and the first unfold is the lazy read. The null-id
// gate is the second line of defence behind the block's own mount guard (`hasLockCodes`): a
// cerradura never migrated to devices has no sequences and the read would be a guaranteed 404.
//
// Sibling of `useLockerLog` and deliberately INDEPENDENT of it: two queries in one disclosure, so
// one failing never takes the other down. No `throwOnError` — a failed read degrades to the inline
// strip with «Reintentar» while the log below keeps working. Retry is left at the client default
// (1): the likely failure is a transient `502 lock_sequences_unavailable` from devices, which a
// single retry can absorb before the agent ever sees the strip.
//
// The codes are opening credentials: they live in the query cache and are rendered on screen, but
// they are never logged, never sent to analytics and never put in a URL.
export function useLockCodes(
  lockId: string | null | undefined,
  open: boolean,
  fetchLockCodes: (lockId: string) => Promise<LockCodes>,
): UseLockCodes {
  const { data, isPending, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['lock-codes', lockId],
    queryFn:
      open && lockId !== null && lockId !== undefined ? () => fetchLockCodes(lockId) : skipToken,
    // Overrides the client's 5-minute default, and this is the one place in the slice where that
    // default is actively wrong. A code table's pointer advances whenever a code is GIVEN, and
    // most of the ways that happens are invisible to the panel: the app handing the «Usuario» a
    // code at pickup start, a second agent on the same «buzón», a courier. The four writes'
    // invalidation only covers the moves the panel itself causes, so under the default an unfold
    // inside the window would serve a sequence the server had already spent — and the agent reads
    // «SIGUIENTE» out loud. Nothing about the cost argues the other way: the read is
    // side-effect-free by contract, so paying for it on every unfold buys correctness for latency.
    staleTime: 0,
  });
  // A permission refusal is the host's to show: it replaces the whole view, so reporting it here
  // as a failed read would only paint the inline strip under the access screen.
  return {
    codes: data,
    isPending,
    isFetching,
    isError: isError && !isPermissionRefused(error),
    refetch: () => void refetch(),
  };
}
